import {
  Component,
  ElementRef,
  AfterViewInit,
  Input,
  OnDestroy,
  ViewChild,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import lottie, { AnimationItem } from 'lottie-web';
import nebulaAnimationDark from './nebula-dark.json';
import nebulaAnimationLight from './nebula-light.json';

@Component({
  selector: 'app-loader',
  templateUrl: './loader.component.html',
  styleUrls: ['./loader.component.scss'],
})
export class LoaderComponent implements AfterViewInit, OnDestroy {
  @Input() duration = 3000;
  /** Texto de estado (i18n). Lo pasa el padre traducido; si no, usa el valor por defecto. */
  @Input() statusText?: string;
  @Output() completed = new EventEmitter<void>();

  @ViewChild('animationContainer', { static: false })
  readonly animationContainer!: ElementRef<HTMLDivElement>;

  private animation: AnimationItem | null = null;
  progress = 0;
  progressWidth = '0%';
  loadingStatus = 'Progreso en Curso ...';
  private progressInterval: number | null = null;
  private statusInterval: number | null = null;
  isDarkMode = false;
  private readonly cdr = inject(ChangeDetectorRef);

  private statusMessages: string[] = ['Progreso en Curso ...'];

  constructor() {
    this.checkTheme();
  }

  /** Aplica el texto de estado traducido que envia el padre (si lo hay). */
  private applyStatusText(): void {
    if (this.statusText) {
      this.loadingStatus = this.statusText;
      this.statusMessages = [this.statusText];
    }
  }

  private checkTheme(): void {
    const isDark = localStorage.getItem('dark_mode') === 'true';
    this.isDarkMode = isDark;
  }

  ngAfterViewInit(): void {
    this.applyStatusText();
    this.initializeAnimation();
    this.startProgressBar();
    this.startStatusRotation();
    window.addEventListener('storage', this.storageEventHandler);
  }

  private storageEventHandler = (event: StorageEvent): void => {
    if (event.key === 'dark_mode') {
      const newIsDark = event.newValue === 'true';
      if (this.isDarkMode !== newIsDark) {
        this.isDarkMode = newIsDark;
        this.reloadAnimation();
        this.cdr.detectChanges();
      }
    }
  };

  private reloadAnimation(): void {
    if (this.animation) {
      this.animation.destroy();
      this.initializeAnimation();
    }
  }

  private startProgressBar(): void {
    const step = 100;
    const totalSteps = this.duration / step;
    const incrementPerStep = 100 / totalSteps;

    this.progress = 0;

    this.progressInterval = window.setInterval(() => {
      if (this.progress < 100) {
        this.progress = Math.min(this.progress + incrementPerStep, 100);
        this.progressWidth = this.progress + '%';
        this.cdr.detectChanges();
      } else {
        this.completeLoading();
      }
    }, step);
  }

  private startStatusRotation(): void {
    let index = 0;
    this.statusInterval = window.setInterval(() => {
      if (this.progress < 95) {
        this.loadingStatus = this.statusMessages[index % this.statusMessages.length];
        index++;
        this.cdr.detectChanges();
      } else if (this.progress >= 95) {
        this.loadingStatus = 'Finalizando...';
        this.cdr.detectChanges();
      }
    }, 2000);
  }

  private completeLoading(): void {
    if (this.progressInterval) window.clearInterval(this.progressInterval);
    if (this.statusInterval) window.clearInterval(this.statusInterval);
    window.removeEventListener('storage', this.storageEventHandler);
    this.completed.emit();
  }

  private initializeAnimation(): void {
    if (!this.animationContainer) return;

    const container = this.animationContainer.nativeElement;
    container.innerHTML = '';

    const animationData = this.isDarkMode ? nebulaAnimationDark : nebulaAnimationLight;

    this.animation = lottie.loadAnimation({
      container: container,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: animationData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid meet',
      },
    });

    this.animation.addEventListener('DOMLoaded', () => {
      setTimeout(() => this.fixSVGDisplay(), 50);
    });
  }

  private fixSVGDisplay(): void {
    const container = this.animationContainer.nativeElement;
    const svg = container.querySelector('svg');

    if (!svg) return;

    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.display = 'block';
    svg.setAttribute('viewBox', '0 0 300 300');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  }

  ngOnDestroy(): void {
    if (this.animation) this.animation.destroy();
    if (this.progressInterval) window.clearInterval(this.progressInterval);
    if (this.statusInterval) window.clearInterval(this.statusInterval);
    window.removeEventListener('storage', this.storageEventHandler);
  }
}
